import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, FileText, Calendar, Package, User, 
  Loader2, AlertCircle, CheckCircle2, Clock
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badges (non-financial info only)
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  pending_bom: 'bg-orange-100 text-orange-800',
  pending_variant_selection: 'bg-purple-100 text-purple-800',
  confirmed: 'bg-blue-100 text-blue-800',
  in_production: 'bg-yellow-100 text-yellow-800',
  ready: 'bg-green-100 text-green-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800'
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_bom: 'Pending BOM',
  pending_variant_selection: 'Pending Variants',
  confirmed: 'Confirmed',
  in_production: 'In Production',
  ready: 'Ready',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

const SOViewOnlyPage = () => {
  const { soId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchOrder = useCallback(async () => {
    if (!soId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/sales-orders/${soId}`, {
        headers: getAuthHeader()
      });
      setOrder(response.data);
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('Failed to load sales order details');
      toast.error('Failed to load sales order');
    } finally {
      setLoading(false);
    }
  }, [soId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const handleBack = () => {
    // Go back to Pending BOM Register
    navigate('/production/pending-bom');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !order) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <AlertCircle className="w-12 h-12 mb-4 text-gray-300" />
            <p>{error || 'Order not found'}</p>
            <Button variant="outline" className="mt-4" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Pending BOM
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="so-view-only-page">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Pending BOM
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6" />
                {order.so_number}
              </h1>
              <p className="text-sm text-gray-500">Sales Order Details (View Only)</p>
            </div>
          </div>
          <Badge className={STATUS_COLORS[order.status] || 'bg-gray-100'}>
            {STATUS_LABELS[order.status] || order.status}
          </Badge>
        </div>

        {/* Summary Cards - Non-financial info only */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <User className="w-4 h-4" />
                <span className="text-xs uppercase">Customer</span>
              </div>
              <p className="font-semibold">{order.customer_name || '-'}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-xs uppercase">SO Date</span>
              </div>
              <p className="font-semibold">{formatDate(order.so_date)}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-xs uppercase">Delivery Date</span>
              </div>
              <p className="font-semibold">{formatDate(order.delivery_date)}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Package className="w-4 h-4" />
                <span className="text-xs uppercase">Total Items</span>
              </div>
              <p className="font-semibold">{order.line_items?.length || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Line Items Table - No pricing info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Line Items</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Line #</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Fulfillment</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>BOM Status</TableHead>
                  <TableHead>Specs/Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {order.line_items?.map((li, idx) => (
                  <TableRow key={li.id || idx}>
                    <TableCell className="font-mono text-sm text-blue-600">
                      {li.item_code || `L${String(idx + 1).padStart(2, '0')}`}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{li.item_name}</div>
                      {li.variant_code && li.variant_code !== li.item_name?.toUpperCase() && (
                        <div className="text-xs text-purple-600 font-mono">{li.variant_code}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {li.fulfillment_type || 'Make'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">{li.quantity}</TableCell>
                    <TableCell>{li.unit || 'NOS'}</TableCell>
                    <TableCell>
                      {li.has_bom ? (
                        <Badge className="bg-green-100 text-green-800 text-xs">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          BOM Ready
                        </Badge>
                      ) : li.fulfillment_type === 'Make' ? (
                        <Badge className="bg-orange-100 text-orange-800 text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          BOM Pending
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">N/A</Badge>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <div className="text-sm text-gray-600 truncate" title={li.specs || li.remarks}>
                        {li.specs || li.remarks || '-'}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Additional Info */}
        {order.remarks && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Remarks</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">{order.remarks}</p>
            </CardContent>
          </Card>
        )}

        {/* Notice for BOM Creator */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <p className="font-medium">Note for BOM Creator:</p>
          <p className="mt-1">
            This is a read-only view. To create BOM for items marked as "BOM Pending", 
            go back to the <a href="/production/pending-bom" className="underline font-medium">Pending BOM Register</a> 
            and click "Create BOM" for the respective item.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SOViewOnlyPage;
